import 'react-json-view-lite/dist/index.css'

import {
  Badge,
  Box,
  Drawer,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  Portal,
  Switch,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useState } from 'react'
import {
  LuBug,
  LuCopy,
  LuNetwork,
  LuSearch,
  LuTrash2,
  LuX,
} from 'react-icons/lu'
import { allExpanded, defaultStyles, JsonView } from 'react-json-view-lite'

import { isDev } from '@/config/env'
import type {
  DumpEntry,
  GqlEntry,
  ManualEntry,
} from '@/contexts/DevProfilerContext'
import { useDevProfilerContext } from '@/contexts/DevProfilerContext'

// ─── Shared helpers ───────────────────────────────────────────────────────────

function isJsonObject(value: unknown): value is object {
  return value !== null && typeof value === 'object'
}

function CopyButton({ value }: { value: unknown }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = () => {
    navigator.clipboard.writeText(JSON.stringify(value, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }
  return (
    <IconButton
      aria-label={copied ? 'Copié !' : 'Copier'}
      size="xs"
      variant="ghost"
      colorPalette={copied ? 'green' : 'gray'}
      onClick={handleCopy}
    >
      <LuCopy />
    </IconButton>
  )
}

function JsonBlock({ value }: { value: unknown }) {
  return (
    <div
      style={{
        borderRadius: 4,
        background: 'var(--chakra-colors-bg-emphasized, #f3f4f6)',
        padding: 8,
        maxHeight: 280,
        overflowY: 'auto',
        fontSize: 11,
      }}
    >
      {isJsonObject(value) ? (
        <JsonView
          data={value}
          shouldExpandNode={allExpanded}
          style={defaultStyles}
        />
      ) : (
        <pre
          style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
        >
          {String(value)}
        </pre>
      )}
    </div>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <Text color="fg.muted" fontSize="sm" textAlign="center" mt={8}>
      {message}
    </Text>
  )
}

// ─── Entry cards ──────────────────────────────────────────────────────────────

function ManualEntryCard({
  entry,
  onRemove,
}: {
  entry: ManualEntry
  onRemove: () => void
}) {
  return (
    <Box borderWidth="1px" borderRadius="md" p={3} bg="bg.panel" w="full">
      <HStack justify="space-between" mb={1}>
        <Text fontWeight="bold" fontSize="sm" truncate>
          {entry.label}
        </Text>
        <HStack gap={0}>
          <CopyButton value={entry.value} />
          <IconButton
            aria-label="Supprimer"
            size="xs"
            variant="ghost"
            colorPalette="red"
            onClick={onRemove}
          >
            <LuTrash2 />
          </IconButton>
        </HStack>
      </HStack>
      <Text color="fg.muted" fontSize="xs" mb={2}>
        {entry.timestamp}
      </Text>
      <JsonBlock value={entry.value} />
    </Box>
  )
}

const TYPE_COLORS: Record<string, string> = {
  query: 'blue',
  mutation: 'orange',
  subscription: 'purple',
}

function GqlEntryCard({
  entry,
  onRemove,
}: {
  entry: GqlEntry
  onRemove: () => void
}) {
  const hasErrors = entry.response?.errors && entry.response.errors.length > 0

  return (
    <Box
      borderWidth="1px"
      borderRadius="md"
      p={3}
      bg="bg.panel"
      w="full"
      borderColor={hasErrors ? 'red.400' : undefined}
    >
      <HStack justify="space-between" mb={2}>
        <HStack gap={1.5}>
          <Badge
            colorPalette={TYPE_COLORS[entry.operationType] ?? 'gray'}
            size="sm"
            variant="subtle"
          >
            {entry.operationType.slice(0, 1).toUpperCase()}
          </Badge>
          <Text fontWeight="bold" fontSize="sm" truncate>
            {entry.operationName}
          </Text>
        </HStack>
        <HStack gap={0}>
          <Text color="fg.muted" fontSize="xs" mr={1}>
            {entry.durationMs}ms
          </Text>
          <CopyButton value={entry} />
          <IconButton
            aria-label="Supprimer"
            size="xs"
            variant="ghost"
            colorPalette="red"
            onClick={onRemove}
          >
            <LuTrash2 />
          </IconButton>
        </HStack>
      </HStack>

      <Text color="fg.muted" fontSize="xs" mb={2}>
        {entry.timestamp}
      </Text>

      <Tabs.Root defaultValue="response" size="sm" variant="subtle">
        <Tabs.List mb={2}>
          <Tabs.Trigger value="response">Response</Tabs.Trigger>
          <Tabs.Trigger value="request">Request</Tabs.Trigger>
          <Tabs.Trigger value="query">Query</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="response">
          {hasErrors && (
            <Box mb={2}>
              <Text fontSize="xs" color="red.500" fontWeight="semibold" mb={1}>
                Errors
              </Text>
              <JsonBlock value={entry.response?.errors} />
            </Box>
          )}
          <JsonBlock value={entry.response?.data ?? null} />
        </Tabs.Content>

        <Tabs.Content value="request">
          <JsonBlock value={entry.variables} />
        </Tabs.Content>

        <Tabs.Content value="query">
          <div
            style={{
              borderRadius: 4,
              background: 'var(--chakra-colors-bg-emphasized, #f3f4f6)',
              padding: 8,
              maxHeight: 280,
              overflowY: 'auto',
              fontSize: 11,
              fontFamily: 'monospace',
            }}
          >
            <pre
              style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {entry.query}
            </pre>
          </div>
        </Tabs.Content>
      </Tabs.Root>
    </Box>
  )
}

// ─── Panel ────────────────────────────────────────────────────────────────────

function DevProfilerPanel() {
  const {
    entries,
    remove,
    clear,
    gqlEnabled,
    setGqlEnabled,
    gqlFilter,
    setGqlFilter,
  } = useDevProfilerContext()
  const [open, setOpen] = useState(false)

  const gqlEntries = entries.filter((e): e is GqlEntry => e.kind === 'gql')
  const filteredGqlEntries = gqlFilter.trim()
    ? gqlEntries.filter((e) =>
        e.operationName.toLowerCase().includes(gqlFilter.toLowerCase())
      )
    : gqlEntries
  const manualEntries = entries.filter(
    (e): e is ManualEntry => e.kind === 'manual'
  )
  const hasEntries = entries.length > 0

  return (
    <>
      <Box
        as="button"
        position="fixed"
        bottom={{ base: 'calc(84px + env(safe-area-inset-bottom))', md: 4 }}
        left={4}
        zIndex={9999}
        h="36px"
        px={2.5}
        borderRadius="full"
        bg="bg.panel"
        color="fg"
        borderWidth="1px"
        borderColor="border"
        // Pink halo (ring) when entries are captured — signals activity without
        // the layout shift a 3px border would cause. Mirrors the rounded, subtly
        // shadowed look of Next's own Dev Tools indicator (now bottom-right).
        boxShadow={
          hasEntries ? '0 0 0 2px var(--chakra-colors-pink-400)' : 'sm'
        }
        transition="box-shadow 0.15s, background 0.15s"
        _hover={{ bg: 'bg.emphasized' }}
        display="flex"
        alignItems="center"
        gap={1.5}
        cursor="pointer"
        onClick={() => setOpen((o) => !o)}
        title="DevProfiler"
      >
        <img src="/icon.svg" width={18} height={18} alt="" />
        <Text
          fontSize="xs"
          fontWeight="bold"
          lineHeight={1}
          letterSpacing="wide"
        >
          DEV
        </Text>
        {hasEntries && (
          <Badge
            colorPalette="pink"
            size="sm"
            variant="solid"
            borderRadius="full"
            minW="18px"
          >
            {entries.length > 99 ? '99+' : entries.length}
          </Badge>
        )}
      </Box>

      <Drawer.Root
        open={open}
        onOpenChange={({ open: o }) => setOpen(o)}
        placement="start"
        size="md"
        modal={false}
      >
        <Portal>
          <Drawer.Positioner pointerEvents="none">
            <Drawer.Content pointerEvents="auto" boxShadow="xl">
              <Drawer.Header borderBottomWidth="0" pb={0}>
                <HStack justify="space-between" w="full">
                  <Drawer.Title>DevProfiler</Drawer.Title>
                  <HStack>
                    {hasEntries && (
                      <IconButton
                        aria-label="Vider tout"
                        size="xs"
                        variant="ghost"
                        colorPalette="red"
                        onClick={clear}
                      >
                        <LuTrash2 />
                      </IconButton>
                    )}
                    <Drawer.CloseTrigger asChild position="static">
                      <IconButton aria-label="Fermer" size="xs" variant="ghost">
                        ✕
                      </IconButton>
                    </Drawer.CloseTrigger>
                  </HStack>
                </HStack>
              </Drawer.Header>

              <Tabs.Root
                defaultValue="graphql"
                display="flex"
                flexDirection="column"
                flex={1}
                overflow="hidden"
              >
                <Tabs.List px={4} pt={2}>
                  <Tabs.Trigger value="graphql" gap={1.5}>
                    <Icon>
                      <LuNetwork />
                    </Icon>
                    GraphQL
                    {gqlEntries.length > 0 && (
                      <Badge
                        colorPalette="blue"
                        size="sm"
                        variant="subtle"
                        ml={1}
                      >
                        {gqlEntries.length}
                      </Badge>
                    )}
                  </Tabs.Trigger>
                  <Tabs.Trigger value="debug" gap={1.5}>
                    <Icon>
                      <LuBug />
                    </Icon>
                    Debug
                    {manualEntries.length > 0 && (
                      <Badge
                        colorPalette="orange"
                        size="sm"
                        variant="subtle"
                        ml={1}
                      >
                        {manualEntries.length}
                      </Badge>
                    )}
                  </Tabs.Trigger>
                </Tabs.List>

                <Tabs.Content
                  value="graphql"
                  overflowY="auto"
                  flex={1}
                  px={4}
                  py={3}
                >
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="xs" color="fg.muted">
                      Intercepter automatiquement
                    </Text>
                    <Switch.Root
                      size="sm"
                      checked={gqlEnabled}
                      onCheckedChange={(e) => setGqlEnabled(e.checked)}
                    >
                      <Switch.HiddenInput />
                      <Switch.Control />
                    </Switch.Root>
                  </HStack>
                  <InputGroup
                    mb={3}
                    startElement={
                      <Icon color="fg.muted">
                        <LuSearch />
                      </Icon>
                    }
                    endElement={
                      gqlFilter ? (
                        <IconButton
                          aria-label="Effacer le filtre"
                          size="xs"
                          variant="ghost"
                          onClick={() => setGqlFilter('')}
                        >
                          <LuX />
                        </IconButton>
                      ) : undefined
                    }
                  >
                    <Input
                      size="sm"
                      placeholder="Filtrer par nom d'opération…"
                      value={gqlFilter}
                      onChange={(e) => setGqlFilter(e.target.value)}
                    />
                  </InputGroup>
                  {filteredGqlEntries.length === 0 ? (
                    <EmptyState
                      message={
                        !gqlEnabled
                          ? 'Interception désactivée.'
                          : gqlFilter
                            ? `Aucun résultat pour "${gqlFilter}".`
                            : 'Aucune opération GraphQL interceptée.'
                      }
                    />
                  ) : (
                    <VStack align="stretch" gap={3}>
                      {filteredGqlEntries.map((entry) => (
                        <GqlEntryCard
                          key={entry.id}
                          entry={entry}
                          onRemove={() => remove(entry.id)}
                        />
                      ))}
                    </VStack>
                  )}
                </Tabs.Content>

                <Tabs.Content
                  value="debug"
                  overflowY="auto"
                  flex={1}
                  px={4}
                  py={3}
                >
                  {manualEntries.length === 0 ? (
                    <EmptyState message="Aucune donnée — appelez dump(label, value) dans votre code." />
                  ) : (
                    <VStack align="stretch" gap={3}>
                      {manualEntries.map((entry) => (
                        <ManualEntryCard
                          key={entry.id}
                          entry={entry}
                          onRemove={() => remove(entry.id)}
                        />
                      ))}
                    </VStack>
                  )}
                </Tabs.Content>
              </Tabs.Root>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </>
  )
}

// ─── Public export ────────────────────────────────────────────────────────────

export function DevProfiler() {
  if (!isDev) return null
  return <DevProfilerPanel />
}
